import { useState } from "react";
import { KeyRound, Loader2, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useApi } from "../../hooks/useApi";
import { ukDateTime } from "../../lib/format";
import { Loading } from "../common";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const tone = { Connected: "bg-[hsl(var(--positive)/0.12)] text-[hsl(var(--positive))]", Rejected: "bg-accent text-accent-foreground", Error: "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]" };

function KeyCard({ it, onChange }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { const { data } = await fn(); onChange(); if (data?.status === "Connected" || data?.status === "Saved") toast.success(data.message); else if (data) toast.error(data.message); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <div className="rounded-lg border bg-card p-5 md:p-6" data-testid={`integration-card-${it.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-md bg-muted"><KeyRound className="h-5 w-5" /></span>
          <div><h3 className="font-heading font-bold">{it.name}</h3><p className="text-xs text-muted-foreground">{it.description}</p></div>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${tone[it.status] || "bg-muted text-muted-foreground"}`} data-testid={`integration-status-${it.key}`}>{it.status}</span>
      </div>
      <div className="mt-4 rounded-md bg-muted/60 px-3 py-2 text-sm" data-testid={`integration-message-${it.key}`}>{it.message}</div>
      <div className="mt-4 grid gap-1 text-sm">
        <div className="flex justify-between"><span className="text-muted-foreground">Saved key</span><span className="font-figure" data-testid={`integration-masked-${it.key}`}>{it.masked || "–"}</span></div>
        {it.last_tested_at && <div className="flex justify-between text-xs text-muted-foreground"><span>Last tested</span><span>{ukDateTime(it.last_tested_at)}</span></div>}
        {it.updated_by && <div className="flex justify-between text-xs text-muted-foreground"><span>Updated by</span><span>{it.updated_by}</span></div>}
      </div>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <Input type="password" placeholder={it.configured ? "Enter a new key to replace" : "Paste API key"} value={value} onChange={(e) => setValue(e.target.value)} data-testid={`integration-input-${it.key}`} />
        <Button disabled={!value.trim() || busy} onClick={() => run(async () => { const r = await api.put(`/admin/integrations/${it.key}`, { value }); setValue(""); return r; })} data-testid={`integration-save-${it.key}`}>
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{it.testable ? "Save & test" : "Save"}
        </Button>
        {it.configured && it.testable && <Button variant="outline" disabled={busy} onClick={() => run(() => api.post(`/admin/integrations/${it.key}/test`))} data-testid={`integration-retest-${it.key}`}><RefreshCw className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
}

export function IntegrationsTab() {
  const { data, loading, reload } = useApi("/admin/integrations", { live: false });
  const [custom, setCustom] = useState({ key: "", value: "" });
  if (loading && !data) return <Loading />;
  const addCustom = async () => {
    const key = custom.key.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
    try { await api.put(`/admin/integrations/${key}`, { value: custom.value, name: custom.key.trim() }); setCustom({ key: "", value: "" }); toast.success("Key saved"); reload(true); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="integrations-tab">
      <p className="text-sm text-muted-foreground max-w-2xl">Keys are stored encrypted and only ever shown masked (last 4 characters). Features that need a key keep working manually until one is added.</p>
      <div className="grid gap-4 lg:grid-cols-2">{data.map((it) => <KeyCard key={it.key} it={it} onChange={() => reload(true)} />)}</div>
      <div className="rounded-lg border border-dashed p-5">
        <h3 className="font-heading font-bold mb-3">Add another key</h3>
        <div className="grid gap-3 sm:grid-cols-[1fr_2fr_auto] items-end">
          <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={custom.key} onChange={(e) => setCustom({ ...custom, key: e.target.value })} placeholder="e.g. Land Registry" data-testid="custom-key-name" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Value</Label><Input type="password" value={custom.value} onChange={(e) => setCustom({ ...custom, value: e.target.value })} data-testid="custom-key-value" /></div>
          <Button onClick={addCustom} disabled={!custom.key.trim() || !custom.value.trim()} data-testid="custom-key-save"><Plus className="mr-1 h-4 w-4" /> Save key</Button>
        </div>
      </div>
    </div>
  );
}
